import { z } from 'zod';
import { FlowTagNameSchema } from './tags.js';
/**
 * Stands in for "no organization" where a tag is addressed.
 *
 * A global tag is stored with a null organization, and a query string cannot carry null, so the
 * tag endpoints read this word as that null.
 */
export const GLOBAL_TAG_ORGANIZATION_ID = 'global';
/** One tag as flow-api serves it. Permissive, because stored tags predate the name validation. */
export const FlowTagRecordSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    category: z.string().nullable(),
    parentTagId: z.uuid().nullable().optional(),
});
/** Query parameters `GET /tags` accepts. */
export const FlowTagSearchQuerySchema = z.object({
    name: z
        .union([z.string(), z.array(z.string())])
        .describe('Return only tags with this name, or with any of these names.')
        .optional(),
    organizationId: z
        .string()
        .describe(`Return only tags of this organization, or of none when '${GLOBAL_TAG_ORGANIZATION_ID}'.`)
        .nullable()
        .optional(),
    category: z.string().describe('Return only tags in this category.').nullable().optional(),
    parentTagId: z.uuid().describe('Return only tags beneath this tag.').optional(),
});
/** Path parameters the endpoints addressing a tag by name accept. */
export const FlowTagNameParamsSchema = z.object({
    name: z.string().min(1).describe('The tag name.'),
});
/**
 * Query parameters `GET /tags/{name}` accepts. A name alone does not identify a tag — the same name
 * may exist in several organizations and categories — so these narrow it.
 */
export const FlowTagByNameQuerySchema = z.object({
    organizationId: z
        .union([z.string(), z.array(z.string())])
        .describe(`The owning organization, or '${GLOBAL_TAG_ORGANIZATION_ID}' for a global tag.`)
        .nullable()
        .optional(),
    category: z.string().describe('The category the tag belongs to.').nullable().optional(),
});
/** Query parameters `DELETE /tags/{name}` accepts, where the owning organization must be stated. */
export const FlowTagDeleteQuerySchema = z.object({
    organizationId: z
        .string()
        .describe(`The owning organization, or '${GLOBAL_TAG_ORGANIZATION_ID}' for a global tag.`)
        .nullable(),
    category: z.string().describe('The category the tag belongs to.').nullable().optional(),
});
/**
 * Body `POST /tags` accepts. A null organization creates a global tag, which takes a permission of
 * its own.
 */
export const FlowTagCreateBodySchema = z.object({
    name: FlowTagNameSchema,
    organizationId: z
        .union([z.uuid(), z.literal(GLOBAL_TAG_ORGANIZATION_ID)])
        .describe(`The owning organization, or '${GLOBAL_TAG_ORGANIZATION_ID}' for a global tag.`)
        .nullable(),
    parentTagId: z.uuid().describe('Tag this one sits beneath.').nullable().optional(),
    category: z.string().describe('Category to file the tag under.').nullable().optional(),
});
/** Path parameters `PUT /tags/{id}` accepts. */
export const FlowTagIdParamsSchema = z.object({
    id: z.uuid().describe('The tag.'),
});
/** Body `PUT /tags/{id}` accepts. The owning organization is not among them: a tag does not move. */
export const FlowTagUpdateBodySchema = z.object({
    name: FlowTagNameSchema.optional(),
    category: z.string().nullable().optional(),
    parentTagId: z.uuid().nullable().optional(),
});
/** A set of tags to match records by, all of them or any of them. */
export const FlowTagFilterSchema = z.object({
    operator: z.enum(['AND', 'OR']),
    tags: z.array(FlowTagRecordSchema.omit({ id: true })),
});
//# sourceMappingURL=tagRequests.js.map